<template>
  <div class="comment_on_video_wrp">
    <!-- Comment Input Form -->
    <div class="comment_input_wrp">
      <div class="row align-items-center">
        <div class="col-auto">
          <span class="comment_self_user_photo" v-if="selfUserInfo">
            <img
              v-if="userThumbnail"
              :src="userThumbnail"
              :alt="userNameInitials"
              class="img-fluid"
            />
            <span v-else class="comment_self_user_name">{{ userNameInitials }}</span>
          </span>
        </div>
        <div class="col pl-0">
          <div class="form-group mb-0">
            <input
              type="text"
              class="form-control"
              placeholder="Add a comment..."
              v-model.trim="commentTextarea"
              @focus="commentButtonGroup = true"
            />
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="row mt-2" v-if="commentButtonGroup">
        <div class="col text-right">
          <button
            type="button"
            class="btn btn-danger mr-3"
            :disabled="isSubmitted"
            @click="cancelComment"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!hasCommentText || isSubmitted"
            @click="postComment"
          >
            <i v-if="isSubmitted" class="fa fa-spinner fa-spin mr-1"></i>
            Comment
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Comments State -->
    <div v-if="isLoadingComments" class="text-center py-4">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
    </div>

    <!-- Comments List -->
    <div class="commented_list_wrp" v-else-if="commentListResults.length > 0">
      <ul class="list_of_commented_user list-unstyled">
        <li v-for="item in commentListResults" :key="item.id">
          <div class="row">
            <div class="col-auto pr-0">
              <div class="commented_user_photo">
                <img
                  v-if="item.user && item.user.profile && item.user.profile.thumbnail"
                  :src="item.user.profile.thumbnail"
                  class="img-fluid"
                />
                <span v-else class="comment_self_user_name">
                  {{ getInitials(item.user ? item.user.first_name : '', item.user ? item.user.last_name : '') }}
                </span>
              </div>
            </div>
            <div class="col">
              <div class="commented_user_info">
                <div class="row">
                  <div class="col">
                    <span class="commented_user_name">
                      {{ item.user ? item.user.first_name : '' }} {{ item.user ? item.user.last_name : '' }}
                    </span>
                  </div>
                  <div class="col-auto">
                    <span class="commented_created_date">{{ item.created_at | LocalDateFormat }}</span>
                  </div>
                </div>
              </div>
              <div class="commented_msg_wrp">
                <span class="commented_msg_text">{{ item.comment }}</span>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Empty State -->
    <p class="no_comment_txt" v-else>No Comments!</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'CommentOnVideoList',
  props: {
    currentVideoData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      commentTextarea: '',
      commentButtonGroup: false,
      isSubmitted: false,
      isLoadingComments: false,
      commentListResults: []
    };
  },
  computed: {
    ...mapGetters(['selfUserInfoGetters']),
    selfUserInfo() {
      const userState = this.selfUserInfoGetters;
      return (userState && userState.data) ? userState.data : null;
    },
    userThumbnail() {
      return (
        this.selfUserInfo &&
        this.selfUserInfo.profile &&
        this.selfUserInfo.profile.thumbnail
      ) || null;
    },
    userNameInitials() {
      if (!this.selfUserInfo) return '';
      return this.getInitials(this.selfUserInfo.first_name, this.selfUserInfo.last_name);
    },
    hasCommentText() {
      return this.commentTextarea.trim().length > 0;
    },
    currentVideoId() {
      return (
        this.currentVideoData &&
        this.currentVideoData.snippet &&
        this.currentVideoData.snippet.resourceId &&
        this.currentVideoData.snippet.resourceId.videoId
      ) || null;
    }
  },
  watch: {
    currentVideoId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchComments(newId);
        }
      }
    }
  },
  methods: {
    getInitials(first = '', last = '') {
      const f = first ? first.charAt(0).toUpperCase() : '';
      const l = last ? last.charAt(0).toUpperCase() : '';
      return `${f} ${l}`.trim();
    },
    cancelComment() {
      this.commentButtonGroup = false;
      this.commentTextarea = '';
    },
    async fetchComments(videoId) {
      if (!videoId) return;
      this.isLoadingComments = true;
      try {
        const res = await this.$store.dispatch('getCommentOnVideoByIDFun', { id: videoId });
        const data = (res && res.data && res.data.data) ? res.data.data : res.data;
        this.commentListResults = (data && data.results) ? data.results : [];
      } catch (err) {
        console.error('Failed to fetch video comments:', err);
        this.commentListResults = [];
      } finally {
        this.isLoadingComments = false;
      }
    },
    async postComment() {
      if (!this.hasCommentText || !this.currentVideoId || !this.selfUserInfo) return;

      this.isSubmitted = true;
      const payload = {
        item_id: this.currentVideoId,
        user: this.selfUserInfo.id,
        comment: this.commentTextarea
      };

      try {
        await this.$store.dispatch('postCommentOnVideoFun', payload);
        this.commentTextarea = '';
        this.commentButtonGroup = false;
        await this.fetchComments(this.currentVideoId);
      } catch (err) {
        console.error('Failed to post comment:', err);
      } finally {
        this.isSubmitted = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.comment_on_video_wrp {
  margin-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  padding: 40px 0;
  font-family: 'MyriadProRegular', 'Lato', sans-serif;

  .comment_self_user_name {
    background: #87cefa;
    width: 40px;
    height: 40px;
    text-align: center;
    line-height: 40px;
    display: block;
    border-radius: 50%;
    color: #000;
    font-size: 16px;
    font-weight: bold;
  }

  .comment_input_wrp {
    .comment_self_user_photo .img-fluid {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    .form-control {
      font-size: 18px;
      background: none;
      border: 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      color: #fff;
      box-shadow: none;
      padding: 10px 0;
      border-radius: 0;

      &:focus {
        border-color: #fff;
      }
    }

    .btn {
      padding: 6px 25px;
      border: none;
      border-radius: 3px;
      height: auto;
      font-size: 16px;
      transition: all ease 0.3s;
      box-shadow: none !important;

      &.btn-primary {
        background: rgba(255, 255, 255, 0.15);
        color: #ffd400;

        &:not(:disabled):hover {
          background: #87cefa;
          color: #22292f;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      &.btn-danger {
        background: rgba(#c82333, 0.5);
        color: #b5b5b5;

        &:hover:not(:disabled) {
          background: #c82333;
          color: #fff;
        }
      }
    }
  }

  .list_of_commented_user {
    margin: 0;
    padding: 0;

    li {
      margin-bottom: 20px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .commented_list_wrp {
    padding-top: 40px;

    .commented_user_photo .img-fluid {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    .commented_user_name {
      font-size: 18px;
      color: #fffcc4;
      line-height: 1;
      font-weight: 600;
    }

    .commented_created_date {
      font-size: 13px;
      color: #959494;
    }

    .commented_msg_wrp {
      margin-top: 5px;

      .commented_msg_text {
        font-size: 14px;
        font-weight: normal;
        font-family: 'georgiaregular', serif;
        color: #d6d6d6;
        display: block;
        line-height: 1.4;
      }
    }
  }

  .no_comment_txt {
    text-align: center;
    margin: 25px 0 0;
    font-size: 16px;
    color: #999;
  }
}
</style>